import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import ReactHighcharts from 'react-highcharts';
import { injectIntl } from 'react-intl';
import getTranslatedText from '../../services/getTranslatedText';
import { tntGreen, tntFadedGreen, tntYellow, tntFadedRed, tntRed } from '../../theme/tntColors';
import { grey400 } from 'material-ui/styles/colors';
import chartHelper from '../../services/chartHelper';
import {
  QUESTION_TYPE_AGREE,
  QUESTION_TYPE_FIVESTARS,
  QUESTION_TYPE_NPS,
  QUESTION_TYPE_SMILEYCOMMENT,
  QUESTION_TYPE_SMILEYQUARTET,
  QUESTION_TYPE_SMILEYQUINTET,
  QUESTION_TYPE_YESNO,
} from '../../constants';



function getCategories(questionType, distribution, intl, language) {
  const yesNoCategories = {
    1: intl.formatMessage({
      id: 'no',
      defaultMessage: 'No',
    }),
    2: intl.formatMessage({
      id: 'yes',
      defaultMessage: 'Yes',
    }),
  };
  return distribution.map((opt) => {
    switch (questionType) {
      case QUESTION_TYPE_FIVESTARS:
      case QUESTION_TYPE_SMILEYQUARTET:
      case QUESTION_TYPE_SMILEYCOMMENT:
        return opt.value;
      case QUESTION_TYPE_YESNO:
        return yesNoCategories[opt.value];
      case QUESTION_TYPE_AGREE:
        return intl.formatMessage({
          id: `agreeQuestionType-${getTranslatedText(opt.text, language)}`,
          defaultMessage: `${opt.value}`,
        });
      default:
        return getTranslatedText(opt.text, language) || opt.value;
    }
  });
}

function getDistributionPercentages(distribution) {
  const sum = distribution.reduce(((acc, opt) => acc + opt.count), 0);
  return distribution.map(opt => Math.round((opt.count / sum) * 100));
}

function getData(questionType, distribution, colorBars, collapseNps) {
  const values = getDistributionPercentages(distribution);
  if (colorBars) {
    switch (questionType) {
      case QUESTION_TYPE_FIVESTARS:
        const starColors = [tntRed, tntFadedRed, tntYellow, tntFadedGreen, tntGreen];
        return values.map((y, i) => {
          return { y, color: starColors[i] };
        });
      case QUESTION_TYPE_SMILEYQUINTET:
        const fiveSmileyColors = [tntRed, tntFadedRed, tntYellow, tntFadedGreen, tntGreen];
        return values.map((y, i) => {
          return { y, color: fiveSmileyColors[i] };
        });
      case QUESTION_TYPE_SMILEYQUARTET:
      case QUESTION_TYPE_SMILEYCOMMENT:
        const smileyColors = [tntRed, tntFadedRed, tntFadedGreen, tntGreen];
        return values.map((y, i) => {
          return { y, color: smileyColors[i] };
        });
      case QUESTION_TYPE_YESNO:
        const yesNoColors = [tntRed, tntGreen];
        return values.map((y, i) => {
          return { y, color: yesNoColors[i] };
        });
      case QUESTION_TYPE_NPS:
        if (collapseNps) {
          const collapsedNpsColors = [tntRed, grey400, tntGreen];
          return values.map((y, i) => {
            return { y, color: collapsedNpsColors[i] };
          });
        } else {
          function getNpsColor(value) {
            if (value <= 6) {
              return tntRed;
            } else if (value >= 7 && value <= 8) {
              return grey400;
            } else {
              return tntGreen;
            }
          }
          return values.map((y, i) => {
            return { y, color: getNpsColor(i) };
          });
        }
      default:
        return values;
    }
  } else {
    return values;
  }
}

const styles = {
  wrapper: {
    padding: 15
  },
};

class DistributionBarChart extends Component {
  render() {

    const { useNumbers } = this.props.widgetConfig || {};
    const intl = this.props.intl;
    const question = this.props.question;
    const answersReport = this.props.answersReport;
    let distribution = answersReport ? answersReport.distribution : [];

    if (question.type === QUESTION_TYPE_NPS && this.props.collapseNps) {
      let counts = [
        {
          value: 1,
          text: [{
            language: null,
            text: intl.formatMessage({ id: 'detractors' }),
          }],
          count: 0,
        }, {
          value: 2,
          text: [{
            language: null,
            text: intl.formatMessage({ id: 'passives' }),
          }],
          count: 0,
        }, {
          value: 3,
          text: [{
            language: null,
            text: intl.formatMessage({ id: 'promoters' }),
          }],
          count: 0,
        },
      ];
      distribution.forEach((opt) => {
        if (opt.value <= 6) {
          counts[0].count += opt.count;
        } else if (opt.value === 7 || opt.value === 8) {
          counts[1].count += opt.count;
        } else if (opt.value >= 9) {
          counts[2].count += opt.count;
        }
      });
      distribution = counts;
    }

    const categories = getCategories(question.type, distribution, intl, this.props.intl.locale);

    const labels = chartHelper.getLabels(question.type, 22, !!useNumbers);

    const data = getData(question.type, distribution, this.props.colorBars, this.props.collapseNps);

    const chartConfig = {
      chart: {
        type: 'bar',
        height: this.props.height - (styles.wrapper.padding * 2),
        width: this.props.width - (styles.wrapper.padding * 2),
      },
      title: null,
      legend: {
        enabled: false,
      },
      tooltip: {
        formatter: function () {
          const index = categories.findIndex(x => x === this.x);
          return intl.formatMessage({
            id: 'numberOfAnswers',
          }, {
            count: distribution[index].count,
          });
        },
      },
      xAxis: {
        categories,
        labels: labels,
      },
      yAxis: {
        visible: false,
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            format: '{y}%'
          },
          pointPadding: 0,
          groupPadding: 0.1,
        },
      },
      series: [{
        data,
      }],
    };
    return (
      <div style={styles.wrapper}>
        <ReactHighcharts
          config={chartConfig}
          domProps={{ id: this.props.uniqueId }}
        />
      </div>
    );
  }
}

export default createFragmentContainer(
  injectIntl(DistributionBarChart),
  graphql`
    fragment DistributionBarChart_question on Question {
      type
    }
    fragment DistributionBarChart_answersReport on AnswersReport {
      distribution {
        value
        count
        text {
          language
          text
        }
      }
    }
  `,
);



// WEBPACK FOOTER //
// ./src/components/DistributionBarChart/DistributionBarChart.js